import React, { useEffect, useState, useContext } from 'react'
import styled from 'styled-components'
import moment from 'moment'
import 'moment/locale/ko'

// components
import ToggleMenu from '../components/dashboard/ToggleMenu'
import Announcement from '../components/dashboard/Announcement'
import QuickMenu from '../components/dashboard/QuickMenu'
import OrdersInformation from '../components/dashboard/OrdersInformation'
import LineChart from '../components/chart/Line'
import OrderChart from '../components/dashboard/OrderChart'
import SaleChart from '../components/dashboard/SaleChart'
import { ToggleBtn } from '../components/atom'
import { CalendarModal, SubmitModal } from '../components'
import TimePicker from '../components/timePicker/TimePicker'
import { Loading } from '../components/atom'
import Button from '../components/atom/button/Button'
import { BrowserView } from 'react-device-detect'

// globals
import * as constants from '../globals/constants'
import * as TYPE from '../globals/type'
import StoreContext from '../globals/StoreContext'

// utils
import { useAxios } from '../utils/useAxios'
import { useDarkMode } from '../utils/useDarkMode'
import { getPeriod } from '../utils/getPeriod'

// styles
import colors from '../styles/colors'

// constants
const QUICK_MENU_TITLE = '빠른메뉴'
const ANNOUNCEMENT_TITLE = '공지'
const INIT_PERIOD = getPeriod(
  moment().subtract(7, 'd').format('YYYY-MM-DD'),
  moment().subtract(1, 'd').format('YYYY-MM-DD'),
  'dd',
)
const API_TYPE = {
  DASHBOARD: 'topNoticeResult',
  STORE: 'storeInfo',
  DELIVERY: 'updateDelivery',
  HAPPY_ORDER: 'updateHappyorder',
}

export default function Dashboard() {
  const { fetchData, loading, data, error } = useAxios()

  //매장 정보 조회 및 초기 매장정보
  const contextData = useContext(StoreContext)
  const { storeInfoData, getStoreInfo } = contextData || {}

  const { isDarkMode } = useDarkMode()
  const [timeDate, setTimeDate] = useState(
    moment(new Date()).format('YYYY-MM-DD HH:mm'),
  )
  const [isMobile, setIsMobile] = useState(false)
  const [overChartIdx, setOverChartIdx] = useState(null)
  const [singleCalDisplay, setSingleCalDisplay] = useState(true)

  // 토글(매장 정보)
  const [toggleItem, setToggleItem] = useState({
    delivery: 'N',
    happyOrder: 'N',
  })
  const [storeItem, setStoreItem] = useState(null)

  // 공지사항, 실시간 주문현황
  const [dashboardItem, setDashboardItem] = useState(null)

  // 주간 주문수
  const [orderChartItem, setOrderChartItem] = useState({
    date: INIT_PERIOD,
    count: Array.from({ length: 7 }, (v, i) => (v = 0)),
  })

  // 주간 매출
  const [saleChartItem, setSaleChartItem] = useState({
    date: INIT_PERIOD,
    count: Array.from({ length: 14 }, (v, i) => (v = 0)),
  })

  // 새로고침
  const [isRefresh, setIsRefresh] = useState(false)

  // 빠른 메뉴 휴무일 변경 관련 / ref - ManageInfo.jsx
  const [startDate, setStartDate] = useState(new Date())
  const [endDate, setEndDate] = useState(new Date())
  const [daysOff, setDaysOff] = useState([])
  const [selectedDates, setSelectedDates] = useState([])
  const [datesToDisplay, setDatesToDisplay] = useState([])
  const [rangeDates, setRangeDates] = useState([])
  const defaultSelected = {
    to: '',
    from: '',
  }
  const [range, setRange] = useState(defaultSelected)

  // 빠른 메뉴 영업시간 변경 관련 / ref - ManageInfo.jsx
  const [allDay, setAllDay] = useState('N')
  const [isDropdownDisabled, setIsDropdownDisabled] = useState(false)
  const [shopStart, setShopStart] = useState([])
  const [shopEnd, setShopEnd] = useState([])
  const [deliveryHoursStart, setDeliveryHoursStart] = useState('')
  const [deliveryHoursEnd, setDeliveryHoursEnd] = useState('')

  // 휴무일
  const [isVisibleDateModal, setIsVisibleDateModal] = useState(false)
  // 영업시간
  const [isVisibleHourModal, setIsVisibleHourModal] = useState(false)

  // 빠른 메뉴 정산내역 표시 여부
  const [isSettlement, setIsSettlement] = useState(true)

  // 디폴트
  const [defaultTime, setDefaultTime] = useState({
    start: '',
    end: '',
  })

  // 로딩 처음에만 보이기 위함
  const [isFirst, setIsFirst] = useState(true)

  const handleResize = () => {
    setIsMobile(window.innerWidth <= 480)
  }

  // 시작
  useEffect(() => {
    getDashboard()
    setShopInfo()
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  //매장정보 조회
  useEffect(() => {
    if (storeInfoData) {
      // 매장정보가 존재할 경우만
      setShopInfo()
    }
  }, [storeInfoData])

  const setShopInfo = () => {
    let resData = storeInfoData?.responseData
    // 매장 정보(배달, 해피오더, 휴무일 관련) 조회
    if (resData?.hasOwnProperty(API_TYPE.STORE)) {
      const {
        deliveryYn,
        happyorderYn,
        closedDay,
        shopHoursStart,
        shopHoursEnd,
        orderHoursStart,
        orderHoursEnd,
      } = resData.storeInfo

      setToggleItem({
        delivery: deliveryYn,
        happyOrder: happyorderYn,
      })

      setStoreItem(resData?.storeInfo)
      setIsSettlement(resData?.hiddenMenuEnableYn === 'N')
      setDeliveryHoursStart(orderHoursStart)
      setDeliveryHoursEnd(orderHoursEnd)

      // 휴무일(있음)
      if (closedDay) {
        const datesArray = closedDay.split('∀')
        const newSelectedDates = datesArray.map(
          (dates) =>
            new Date(
              `${dates.slice(0, 4)}-${dates.slice(4, 6)}-${dates.slice(-2)}`,
            ),
        )

        setSelectedDates(newSelectedDates)
      }

      // 휴무일(없음)
      if (!closedDay) {
        setSelectedDates([])
      }

      // 영업시간
      if (shopHoursStart && shopHoursEnd) {
        setShopStart(shopHoursStart)
        setShopEnd(shopHoursEnd)
      }

      // 로컬 스토리지 추가(해피오더 유무 변경)
      localStorage.setItem('happyorderYn', happyorderYn)
      localStorage.setItem('closedDay', closedDay)
      localStorage.setItem('shopHoursStart', shopHoursStart)
      localStorage.setItem('shopHoursEnd', shopHoursEnd)
      window.dispatchEvent(new Event('storage'))
    }
  }

  // [data]
  useEffect(() => {
    let resCode = data?.responseCode
    let resData = data?.responseData

    switch (resCode) {
      // 성공
      case TYPE.API_RESPONSE.SUCCESS:
        // 대시보드 정보(나머지) 조회
        if (resData.hasOwnProperty(API_TYPE.DASHBOARD)) {
          // 로컬 스토리지 추가(새로운 공지사항 체크)
          localStorage.setItem('isNewNotice', resData?.isNewNotice)
          window.dispatchEvent(new Event('storage'))

          setDashboardItem({
            announcement: resData?.topNoticeResult ?? null, // 공지사항
            orderInfo: resData?.realTimeOrderResult ?? null, // 실시간 주문 현황
            isNewNotice: resData?.topNoticeResult?.readYn !== 'Y', // 새로운 공지사항 유무(변경: 해당 공지사항 읽음 여부 체크)
          })

          // 주간 주문수 관련
          let orderDate = []
          let orderCount = []

          let orderStartDate = moment().subtract(7, 'd').format('YYYY-MM-DD')
          let orderEndDate = moment().subtract(1, 'd').format('YYYY-MM-DD')
          let orderPeriod = getPeriod(
            orderStartDate,
            orderEndDate,
            'YYYY-MM-DD',
          )

          for (let i = 0; i < orderPeriod.length; i++) {
            let foundItem = resData?.orderCountOfWeekResult.find(
              (e) => e.date === orderPeriod[i],
            )

            orderDate.push(moment(orderPeriod[i]).format('dd'))
            orderCount.push(foundItem ? foundItem.dayOrderCount : 0)
          }

          setOrderChartItem({ date: orderDate, count: orderCount })

          // 주간 매출 관련
          let saleDate = []
          let saleCount = []

          let saleStartDate = moment().subtract(14, 'd').format('YYYY-MM-DD')
          let saleEndDate = moment().subtract(1, 'd').format('YYYY-MM-DD')
          let salePeriod = getPeriod(saleStartDate, saleEndDate, 'YYYY-MM-DD')

          for (let i = 0; i < salePeriod.length; i++) {
            let foundItem = resData?.salePriceOfWeekResult.find(
              (e) => e.date === salePeriod[i],
            )

            saleDate.push(moment(salePeriod[i]).format('dd'))
            saleCount.push(foundItem ? foundItem.daySalePrice / 100000 : 0)
          }

          setSaleChartItem({ date: saleDate, count: saleCount })
        }
        // 배달, 해피오더 변경
        else {
          getStoreInfo()
        }

        break

      // 실패
      case TYPE.API_RESPONSE.FAILURE:
      default:
        break
    }
  }, [data])

  // 시간변경 모달 꺼지면 디폴트 시간 초기화
  useEffect(() => {
    if (
      !isVisibleHourModal &&
      (defaultTime.start !== '' || defaultTime.end !== '')
    ) {
      // off 처리된 모달
      // 변경된 시간 원래대로 돌려야함

      setShopStart(defaultTime.start)
      setShopEnd(defaultTime.end)

      setAllDay('N')
      setDefaultTime({
        start: '',
        end: '',
      })
    }
  }, [isVisibleHourModal])

  // 공지, 주문현황, 차트 조회
  const getDashboard = () => {
    const params = {
      // 해당 값에 따라 응답 값이 달라짐
      // false - 주문현황, 주문수, 주간매출 추가
      isNoticeDetail: false,
    }

    fetchData(constants.API.DASHBOARD, 'GET', null, params)
  }

  // 배달 변경
  const updateDelivery = (flag) => {
    setIsFirst(false)

    const body = {
      deliveryStatus: flag,
    }

    fetchData(constants.API.STORE_DELIVERY, 'PATCH', body)
  }

  // 해피오더 변경
  const updateHappyOrder = (flag) => {
    setIsFirst(false)

    // body
    const body = {
      hoStatus: flag,
    }

    fetchData(constants.API.STORE_HAPPY_ORDER, 'PATCH', body)
  }

  const handleRefresh = () => {
    setIsFirst(false)
    setIsRefresh(true)
    getDashboard()

    setTimeout(() => {
      setIsRefresh(false)
    }, 2000)

    setTimeDate(moment(new Date()).format('YYYY-MM-DD HH:mm'))
  }

  // 휴무일
  const handleClosedDay = () => {
    setIsVisibleDateModal(true)
  }

  // 영업시간
  const handleBusinessHour = () => {
    if (shopStart !== '' && shopEnd !== '') {
      // 24시간인 경우
      if (shopStart === '0000' && (shopEnd === '2400' || shopEnd === '0000')) {
        setAllDay('Y')
      }

      setDefaultTime({
        start: shopStart,
        end: shopEnd,
      })
    } else {
      setDefaultTime({
        start: '',
        end: '',
      })
    }

    setIsVisibleHourModal(true)
  }

  useEffect(() => {
    if (allDay == 'Y') {
      setIsDropdownDisabled(true)
      setShopStart('0000')
      setShopEnd('2400')
    }

    if (allDay == 'N') {
      setIsDropdownDisabled(false)
    }
  }, [allDay])

  // 휴무일 설정
  const updateDaysOff = () => {
    if (!range.to || range.to == null) {
      if (!singleCalDisplay) {
        alert('휴무일 기간을 선택 해 주세요.')
        return
      }
    }

    setIsVisibleDateModal(false)

    const daysOffCombined = datesToDisplay.concat(rangeDates)
    const dateStrings = daysOffCombined
      .map((date) => moment(date).format('YYYYMMDD'))
      .sort()
    const uniqueDateStrings = [...new Set(dateStrings)]
    let totalDaysOff = []

    for (let i = 0; i < uniqueDateStrings.length; i++) {
      totalDaysOff +=
        uniqueDateStrings.length !== i + 1
          ? `${uniqueDateStrings[i]}∀`
          : `${uniqueDateStrings[i]}`
    }

    if (totalDaysOff.length == 0) {
      totalDaysOff = ''
    }

    const {
      tel,
      mobile,
      shopHoursStart,
      shopHoursEnd,
      orderHoursStart,
      orderHoursEnd,
    } = storeItem

    if (totalDaysOff.length == 0) {
      totalDaysOff = ''
    }

    const reqData = {
      tel,
      mobile,
      shopHoursStart,
      shopHoursEnd,
      orderHoursStart,
      orderHoursEnd,
      closedDay: Array.isArray(totalDaysOff) ? '' : totalDaysOff,
    }

    fetchData(
      constants.API.STORES_INFO,
      'PUT', // method
      reqData, // body
    )

    setRange({
      to: '',
      from: '',
    })
  }

  // 매장운영시간 변경
  const updateHours = () => {
    if (isNaN(shopStart) || isNaN(shopEnd)) return alert('시간을 설정해주세요.')

    const { tel, mobile, orderHoursStart, orderHoursEnd, closedDay } = storeItem

    const newShopHoursEnd = shopEnd === '0000' ? '2400' : shopEnd

    const newDeliveryHoursEnd =
      deliveryHoursEnd === '0000' ? '2400' : deliveryHoursEnd

    if (
      deliveryHoursStart < shopStart ||
      newDeliveryHoursEnd > newShopHoursEnd
    ) {
      alert('배달가능시작시간은 매장운영시작시간보다 클 수 없습니다.')
      return
    }

    const reqData = {
      tel,
      mobile,
      shopHoursStart: shopStart,
      shopHoursEnd: newShopHoursEnd,
      orderHoursStart,
      orderHoursEnd,
      closedDay,
    }

    fetchData(
      constants.API.STORES_INFO,
      'PUT', // method
      reqData, // body
    )

    setIsVisibleHourModal(false)
    setIsDropdownDisabled(false)
    setAllDay(false)
  }

  const handleStartChange = (hour, minute) => {
    setShopStart(hour.value + minute.value)
  }

  const handleEndChange = (hour, minute) => {
    setShopEnd(hour.value + minute.value)
  }

  return (
    <Container>
      {loading && isFirst ? (
        <Loading />
      ) : (
        <>
          <BrowserView>
            <RemoteBox>
              <Button onClick={() => window.open('http://939.co.kr/secta9')}>
                원격지원
              </Button>
              <p className="remoteGuideTxt">
                *운영시간 : 평일 09시~18시 (점심시간 11:30~13:00) <br />
                토, 일, 공휴일 휴무
              </p>
            </RemoteBox>
          </BrowserView>
          <ToggleAndAnnouncementWrapper>
            <ToggleMenu
              toggleData={toggleItem}
              updateDelivery={updateDelivery}
              updateHappyOrder={updateHappyOrder}
            />
            <Announcement
              title={ANNOUNCEMENT_TITLE}
              announcementData={dashboardItem?.announcement}
              isNew={dashboardItem?.isNewNotice}
            />
          </ToggleAndAnnouncementWrapper>

          <QuickAndInfoWrapper>
            <QuickMenu
              title={QUICK_MENU_TITLE}
              isSettlement={!isSettlement}
              handleClosedDay={handleClosedDay}
              handleBusinessHour={handleBusinessHour}
            />
            {!isSettlement && (
              <OrdersInformation
                orderInfoData={dashboardItem?.orderInfo}
                timeDate={timeDate}
                handleRefresh={handleRefresh}
                isRefresh={isRefresh}
              />
            )}
          </QuickAndInfoWrapper>
          {!isSettlement && (
            <ChartContainer>
              <OrderChartWrapper
                onMouseOver={() => setOverChartIdx(1)}
                onMouseOut={() => setOverChartIdx(null)}
              >
                <TitleWrapper isMargin={true} isFirst={true}>
                  주간 주문수
                </TitleWrapper>

                <OrderChart
                  chartItem={orderChartItem}
                  isMobile={isMobile}
                  overChartIdx={overChartIdx}
                  isDarkMode={isDarkMode}
                />
              </OrderChartWrapper>

              <SaleChartWrapper
                onMouseOver={() => setOverChartIdx(2)}
                onMouseOut={() => setOverChartIdx(null)}
              >
                <TitleWrapper>주간 매출</TitleWrapper>
                <LabelWrapper>
                  <WeekLabel>
                    <div>
                      <span />
                      <span>이번주</span>
                    </div>

                    <div>
                      <span />
                      <span>지난주</span>
                    </div>
                  </WeekLabel>

                  <UnitLabel>(단위:10만원)</UnitLabel>
                </LabelWrapper>
                <SaleChart
                  chartItem={saleChartItem}
                  isMobile={isMobile}
                  overChartIdx={overChartIdx}
                  isDarkMode={isDarkMode}
                />
              </SaleChartWrapper>
            </ChartContainer>
          )}
        </>
      )}
      <CalendarModal
        title={'휴무일'}
        show={isVisibleDateModal}
        handleClose={() => setIsVisibleDateModal(false)}
        handleSubmit={updateDaysOff}
        setStartDate={setStartDate}
        startDate={startDate}
        setEndDate={setEndDate}
        endDate={endDate}
        setSelectedDates={setSelectedDates}
        selectedDates={selectedDates}
        range={range}
        setRange={setRange}
        rangeDates={rangeDates}
        setRangeDates={setRangeDates}
        daysOffSetting={true}
        datesToDisplay={datesToDisplay}
        setDatesToDisplay={setDatesToDisplay}
        setSingleCalDisplay={setSingleCalDisplay}
      />
      <SubmitModal
        title={'매장운영시간'}
        show={isVisibleHourModal}
        handleClose={() => setIsVisibleHourModal(false)}
        handleSubmit={updateHours}
      >
        <ToggleButton
          yn={allDay}
          setYn={setAllDay}
          id={'hour'}
          toggleText={'24hr'}
          width={'90'}
        />
        <TimeSettingWrapper>
          <LabelText>시작</LabelText>
          <TimePicker
            onTimeChange={handleStartChange}
            isDropdownDisabled={isDropdownDisabled}
            defaultStart={defaultTime.start}
            type={'start'}
          />
        </TimeSettingWrapper>
        <TimeSettingWrapper>
          <LabelText>종료</LabelText>
          <TimePicker
            onTimeChange={handleEndChange}
            isDropdownDisabled={isDropdownDisabled}
            defaultEnd={defaultTime.end}
            type={'end'}
          />
        </TimeSettingWrapper>
      </SubmitModal>
    </Container>
  )
}

const Container = styled.div`
  width: 100%;
  max-width: 984px;
  align-self: center;
  padding-top: 23px;

  @media ${(props) => props.theme.tablet} {
    padding-top: 0;
  }
`

const ToggleAndAnnouncementWrapper = styled.div`
  width: 100%;
  max-width: 984px;
  display: flex;
  margin-bottom: 30px;

  @media ${(props) => props.theme.tablet} {
    margin-bottom: 20px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }
`

const QuickAndInfoWrapper = styled.div`
  width: 100%;
  max-width: 984px;
  display: flex;
  margin-bottom: 30px;

  @media ${(props) => props.theme.tablet} {
    margin-bottom: 20px;
  }

  @media (max-width: 800px) {
    flex-direction: column;
  }
`

const ChartContainer = styled.div`
  width: 100%;
  max-width: 984px;
  display: flex;

  @media (max-width: 800px) {
    flex-direction: column;
  }

  @media ${(props) => props.theme.mobile} {
    align-items: center;
  }
`

const OrderChartWrapper = styled.div`
  width: 100%;
  min-width: 320px;
  max-width: 477px;
  height: 366px;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.1);
  margin-right: 30px;
  padding: 35px 30px 35px 15px;

  @media ${(props) => props.theme.tablet} {
    margin-right: 20px;
    padding: 35px 30px 35px 15px;
    max-width: 482px;
  }

  @media (max-width: 800px) {
    align-self: center;
    margin: 0 0 20px;
  }

  @media ${(props) => props.theme.mobile} {
    margin-right: 0px;
    max-width: 320px;
    height: 319px;
    margin-bottom: 20px;
    padding: 23px 20px 22px 5px;
  }

  div {
    font-size: 16px;
    font-weight: 700;
    color: ${colors.BK};
  }
`

const SaleChartWrapper = styled.div`
  width: 100%;
  min-width: 320px;
  max-width: 477px;
  height: 366px;
  display: flex;
  flex-direction: column;
  border-radius: 15px;
  box-shadow: 0px 3px 12px rgba(0, 0, 0, 0.1);
  padding: 35px 30px 35px 15px;

  @media ${(props) => props.theme.tablet} {
    padding: 35px 30px 35px 15px;
  }

  @media (max-width: 800px) {
    align-self: center;
    max-width: 482px;
  }

  @media ${(props) => props.theme.mobile} {
    padding: 23px 20px 19px 5px;
    max-width: 320px;
    height: 316px;
  }
`

const TitleWrapper = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.BK};

  margin-bottom: ${(props) => (props.isMargin ? '44px' : '0px')};
  margin-left: ${(props) => (props.isFirst ? '15px' : '15px')};

  @media ${(props) => props.theme.tablet} {
    margin-bottom: ${(props) => (props.isMargin ? '47px' : '0px')};
  }

  @media ${(props) => props.theme.mobile} {
    margin-bottom: ${(props) => (props.isMargin ? '41px' : '0px')};
  }
`

const LabelWrapper = styled.div`
  margin-top: 17px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-bottom: -15px;
`

const WeekLabel = styled.div`
  display: flex;

  div {
    display: flex;
  }

  div:nth-child(1) {
    margin-right: 32px;

    span:nth-child(1) {
      background-color: ${colors.mainG};
    }
  }

  div:nth-child(2) {
    span:nth-child(1) {
      background-color: ${colors.D3};
    }
  }

  span:nth-child(1) {
    width: 10px;
    height: 10px;
    border-radius: 5px;
    margin-right: 5px;
  }

  span:nth-child(2) {
    font-size: 12px;
    color: ${colors.D3};
  }
`

const UnitLabel = styled.div`
  margin-top: 16px;
  font-size: 12px;
  color: ${colors.D3};
`

const ToggleButton = styled(ToggleBtn)`
  align-self: end;
  margin-bottom: 20px;
`

const TimeSettingWrapper = styled.div`
  display: flex;
  margin-bottom: 15px;
`

const LabelText = styled.span`
  display: flex;
  align-items: center;
  margin: 2px 20px 2px 5px;
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: normal;
  letter-spacing: normal;
  text-align: left;
  color: ${colors.D3};
`
const RemoteBox = styled.div`
  display: flex;
  flex-direction: column;
  width: 135px;
  float: right;
  margin-bottom: 20px;
  text-align: center;
  .remoteGuideTxt {
    font-size: 12px;
    color: #000;
    margin-top: 10px;
    line-height: 1.5;
  }
`
